import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { QuoteTaxeEntity } from 'src/app/domain/entities/quotes/quote-taxe.entity';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaSwitchComponent } from 'ra-ui';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import { RaInputComponent } from 'src/app/shared/components/ra-input/ra-input.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import {
    RaSelectModel,
    RaSelectOptionModel,
} from 'src/app/shared/components/ra-select/model/ra-select.model';
import { RaSelectComponent } from 'src/app/shared/components/ra-select/ra-select.component';
import { parseLocalizedNumberInput } from 'src/app/shared/utils/number-input.util';

/** Modo da taxa: valor fixo em dinheiro ou percentual (ex.: 20 = 20%). */
type TaxeMode = 'fixed' | 'percentage';

@Component({
    selector: 'modal-quote-taxe-form',
    templateUrl: 'modal-quote-taxe-form.component.html',
    styleUrl: 'modal-quote-taxe-form.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RaModalComponent,
        RaButtonComponent,
        RaInputComponent,
        RaSelectComponent,
        RaSwitchComponent,
        TranslatePipe,
    ],
})
export class ModalQuoteTaxeFormComponent {
    private _translate = inject(TranslateService);

    /** Taxa em edição — quando `null`, o modal está no modo criar. */
    public taxe = input<QuoteTaxeEntity | null>(null);
    public loading = input<boolean>(false);
    public currencyCode = input<string | null | undefined>();
    public locale = input<string>('pt-BR');
    public close = output<void>();
    public saveNew = output<{ taxe: QuoteTaxeEntity; saveInCatalog: boolean }>();
    public saveEdit = output<{ name: string; price: number }>();

    public isEdit = computed(() => !!this.taxe());

    public form = new FormGroup({
        mode: new FormControl<TaxeMode>('fixed', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        name: new FormControl('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        price: new FormControl<number | null>(null, {
            validators: [Validators.required, Validators.min(0.01)],
        }),
        saveInCatalog: new FormControl(false, { nonNullable: true }),
    });

    public modeSelectModel: RaSelectModel;

    constructor() {
        this.modeSelectModel = this._buildModeSelectModel();
        this._applyTranslatedSelectTexts();

        effect(() => {
            const taxe = this.taxe();

            if (taxe) {
                const mode = taxe.mode ?? 'fixed';
                this.form.patchValue(
                    {
                        mode,
                        name: taxe.name ?? '',
                        price: taxe.price ?? null,
                        saveInCatalog: false,
                    },
                    { emitEvent: false }
                );
                this._updateModeSelected(mode);
                this._applyPriceValidators(mode);
                // No modo edição o tipo da taxa não pode ser alterado.
                this.form.controls.mode.disable({ emitEvent: false });
            } else {
                this.form.reset(
                    {
                        mode: 'fixed',
                        name: '',
                        price: null,
                        saveInCatalog: false,
                    },
                    { emitEvent: false }
                );
                this._updateModeSelected('fixed');
                this._applyPriceValidators('fixed');
                this.form.controls.mode.enable({ emitEvent: false });
            }
        });
    }

    public onModeSelect(option: RaSelectOptionModel): void {
        const mode = option.value as TaxeMode;
        this.form.controls.mode.setValue(mode);
        this._updateModeSelected(mode);
        this._applyPriceValidators(mode);
        // Dinheiro e percentual não são o mesmo número: trocar de modo zera o valor
        // em vez de reinterpretar "20" como R$ 20,00 ou 20%.
        this.form.controls.price.setValue(null);
    }

    public get priceLabelKey(): string {
        return this.isPercentageMode
            ? 'document.edit.quote.taxeModal.valuePercentage'
            : 'document.edit.quote.taxeModal.valueFixed';
    }

    /** Percentual digita percent (0–100); fixa digita dinheiro na moeda do documento. */
    public get isPercentageMode(): boolean {
        return this.form.controls.mode.value === 'percentage';
    }

    public get priceInputType(): string {
        return this.isPercentageMode ? 'percent' : 'currency';
    }

    public get showPercentMaxError(): boolean {
        return !!this.form.controls.price.errors?.['max'];
    }

    public onSave(): void {
        if (this.form.invalid) return;

        const raw = this.form.getRawValue();
        const price = parseLocalizedNumberInput(raw.price) ?? 0;

        if (this.isEdit()) {
            this.saveEdit.emit({
                name: raw.name,
                price,
            });
            return;
        }

        const taxe = new QuoteTaxeEntity();
        taxe.name = raw.name;
        taxe.mode = raw.mode;
        taxe.price = price;

        this.saveNew.emit({ taxe, saveInCatalog: raw.saveInCatalog });
    }

    // No modo percentual o valor é um percent (0 < v <= 100); no fixo é dinheiro (> 0).
    private _applyPriceValidators(mode: TaxeMode): void {
        const validators = [Validators.required, Validators.min(0.01)];
        if (mode === 'percentage') {
            validators.push(Validators.max(100));
        }
        this.form.controls.price.setValidators(validators);
        this.form.controls.price.updateValueAndValidity({ emitEvent: false });
    }

    private _applyTranslatedSelectTexts(): void {
        const tr = this._translate;
        this.modeSelectModel.placeholder = tr.instant('document.edit.quote.taxeModal.mode');
        const opts: { idx: number; key: string }[] = [
            { idx: 0, key: 'document.edit.quote.modeFixed' },
            { idx: 1, key: 'document.edit.quote.modePercentage' },
        ];
        opts.forEach(({ idx, key }) => {
            const opt = this.modeSelectModel.listOptions[idx];
            if (opt) {
                opt.description = tr.instant(key);
            }
        });
    }

    private _buildModeSelectModel(): RaSelectModel {
        const model = new RaSelectModel();
        model.placeholder = '';
        model.loading = false;
        model.disabled = false;
        model.listOptions = [
            { description: '', value: 'fixed', selected: true },
            { description: '', value: 'percentage', selected: false },
        ];
        return model;
    }

    private _updateModeSelected(mode: TaxeMode): void {
        this.modeSelectModel.listOptions.forEach(opt => {
            opt.selected = opt.value === mode;
        });
    }
}
